import { useImportRunProblems } from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import {
  buildRunReport,
  FIXER_HEADING,
  problemLine,
} from "@via/pages/projects/metadata/configuration/data-import/utils/run-report";
import { formatDateString } from "@via/utils/helpers";
import type { VmetaImportRunSummary } from "common";
import { Fragment, useState, type FC } from "react";

const SOURCE_LABEL = {
  legacy: "Legacy",
  "foundry-connect": "Foundry Connect",
} as const;

const buttonClass =
  "rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50";

type RunLogProps = {
  logs: VmetaImportRunSummary[];
  canvasId: string;
  configId: string;
  configName: string;
  /** From the project the page already loaded; empty when it is not at hand. */
  projectName?: string;
  platform: string | null;
};

const RunDetails: FC<RunLogProps & { run: VmetaImportRunSummary }> = ({
  run,
  canvasId,
  configId,
  configName,
  projectName,
  platform,
}) => {
  const { data, isLoading, isError } = useImportRunProblems(
    canvasId,
    configId,
    run.runId,
    true
  );
  const [copied, setCopied] = useState(false);

  if (isLoading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (isError || !data)
    return (
      <p className="text-sm text-gray-500">
        The problems for this run could not be loaded.
      </p>
    );
  if (!data.saved)
    return (
      <p className="text-sm text-gray-500">
        Details could not be saved for this run.
      </p>
    );

  const copy = async () => {
    const text = buildRunReport({
      configName,
      projectName: projectName ?? "",
      platform,
      run,
      problems: data.problems,
      problemsTruncated: data.problemsTruncated,
      totals: data.totals,
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-3">
      {run.message && <p className="text-sm text-gray-700">{run.message}</p>}
      {(["data", "setup"] as const).map(fixer => {
        const group = data.problems.filter(p => p.fixer === fixer);
        if (group.length === 0) return null;
        return (
          <div key={fixer}>
            <h4 className="text-sm font-semibold text-gray-900">
              {FIXER_HEADING[fixer]}
            </h4>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              {group.map((p, i) => (
                <li key={i}>{problemLine(p)}</li>
              ))}
            </ul>
          </div>
        );
      })}
      {data.problemsTruncated > 0 && (
        <p className="text-sm text-gray-500">
          and {data.problemsTruncated} more not shown
        </p>
      )}
      <div className="flex items-center gap-3">
        <button type="button" className={buttonClass} onClick={copy}>
          Copy report
        </button>
        {copied && (
          <span role="status" className="text-xs text-gray-500">
            Copied
          </span>
        )}
      </div>
    </div>
  );
};

const RunLog: FC<RunLogProps> = props => {
  const { logs } = props;
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section
      aria-labelledby="import-run-log-heading"
      className="space-y-3 border-t border-gray-200 pt-6">
      <h3
        id="import-run-log-heading"
        className="text-base font-semibold text-gray-900">
        Run log
      </h3>
      {logs.length === 0 ? (
        <p className="text-sm text-gray-500">No runs yet.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left font-semibold text-gray-900">
              <th className="py-1 pr-4">Status</th>
              <th className="py-1 pr-4">Finished</th>
              <th className="py-1 pr-4">Source</th>
              <th className="py-1 pr-4">Inserted</th>
              <th className="py-1 pr-4">Updated</th>
              <th className="py-1 pr-4">Skipped</th>
              <th className="py-1 pr-4">Errors</th>
              <th className="py-1">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {logs.map((log, i) => {
              const legacy = log.source === "legacy";
              const unrecorded = log.unrecordedErrorCount ?? 0;
              const expandable =
                legacy || (log.problemCount ?? 0) > 0 || unrecorded > 0;
              const expanded = open === i;
              return (
                <Fragment key={i}>
                  <tr>
                    <td className="py-1 pr-4">{log.status}</td>
                    <td className="py-1 pr-4">
                      {log.dateEnded
                        ? formatDateString(new Date(log.dateEnded), "LLL")
                        : ""}
                    </td>
                    <td className="py-1 pr-4">{SOURCE_LABEL[log.source]}</td>
                    <td className="py-1 pr-4">{log.counts.inserted ?? ""}</td>
                    <td className="py-1 pr-4">{log.counts.updated ?? ""}</td>
                    <td className="py-1 pr-4">{log.counts.skipped ?? ""}</td>
                    <td className="py-1 pr-4">
                      {(log.counts.insertErrors ?? 0) +
                        (log.counts.updateErrors ?? 0) +
                        (log.counts.parentUpdateErrors ?? 0) +
                        (log.counts.flagErrors ?? 0) || ""}
                    </td>
                    <td className="py-1">
                      {expandable && (
                        <button
                          type="button"
                          className={buttonClass}
                          aria-expanded={expanded}
                          onClick={() => setOpen(expanded ? null : i)}>
                          {expanded
                            ? "Hide"
                            : legacy
                              ? "Show details"
                              : "Show problems"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={8} className="py-2">
                        {legacy ? (
                          <p className="text-sm text-gray-500">
                            Details were not recorded for runs before the new
                            importer.
                          </p>
                        ) : (log.problemCount ?? 0) === 0 && unrecorded > 0 ? (
                          <p className="text-sm text-gray-500">
                            {unrecorded} errors were recorded before details
                            were available.
                          </p>
                        ) : (
                          <RunDetails {...props} run={log} />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default RunLog;
